import { useEffect, useState } from "react";
import { isEmpty, sleep } from "../Util";
import { useLocation, useNavigate } from "react-router-dom";
import {cancelTransaction, fetchCoverstatus, getStrongVerificationOptions, getQuote, handleException, verifyTransaction, patchTransaction, isTransactionProcessing } from "../store/Actions";
import { useSelector } from "react-redux";

import { useTranslation } from "react-i18next";
import Layout from "./common/Layout";
import Error from "./common/Error";
import Back from "./common/Back";
import Result from "./common/Result";
import Loader from "./common/Loader";

const Quotation  = () => { 

  const MAX_RETRY_ASSESSING = 30
  const WAITING_BEFORE_REDIRECT_MS = 10000
  const MIN_WAITING_BEFORE_REQUIREMENT_MS = 5000 

  const [isResourceLoaded, setIsResourceLoaded] = useState(false)
  const [requirement, setRequirement] = useState(undefined)
  const [callbackUrl, setCallbackUrl] = useState(undefined)
  const [quoteStatus, setQuoteStatus] = useState(undefined)
  const [retryAssessing, setRetryAssessing] = useState(0)
  const [assessingAt, setAssessingAt] = useState(undefined)
  const [shouldPatchTransaction, setShouldPatchTransaction] = useState(false)

  const navigate = useNavigate();
  const location = useLocation();

  const error = useSelector((state) => state.Reducer.error)
  const isFraudEnabled = useSelector((state) => state.Reducer.settings?.isFraudCheckEnabled)
  const successUrl = useSelector((state) => state.Reducer.successCallbackUrl)
  const failureUrl = useSelector((state) => state.Reducer.failureCallbackUrl)

  useEffect(() => {      
    window.addEventListener("beforeunload", cancelTransaction);
    return () => window.removeEventListener("beforeunload", cancelTransaction);
  }, []);

  useEffect(() => {
    const handleInitQuotation = async () => {
      const { shouldPatchTransaction, buyer } = location.state || {}
      setShouldPatchTransaction(shouldPatchTransaction)
      if (shouldPatchTransaction  ) {
        await patchTransaction({
              buyerId : buyer.id, 
              companyName : buyer.name,
              countryCode : buyer.address.countryCode, 
              companyIdentifierTypeCode : buyer.mainIdentifierTypeCode,
              companyIdentifierValue : buyer.mainIdentifierValue})
      }
      if (isFraudEnabled)
        await getStrongVerificationOptions()
      await handleGetQuote();  
      setIsResourceLoaded(true);  
    }  
    handleInitQuotation()
  }, [])

  useEffect(() => {
      if (isResourceLoaded && isEmpty(error) && !isAssessing())  {
        if (!isEmpty(callbackUrl)) {
          sleep(WAITING_BEFORE_REDIRECT_MS).then(id =>  {
            clearTimeout(id)
            window.location.replace(callbackUrl)
          })
        } else if (isQuoted()) {
          switch(requirement) {
            case "Otp" : verifyTransaction(() => navigate("/OtpCheck")); break
            case "StrongVerification" : navigate("/CheckOptions"); break
          }
        }
      }
  }, [isResourceLoaded, error, quoteStatus, callbackUrl, requirement]); 

  useEffect( () => {
    const retryFetchCoverStatus = async () => {
      let waitingTime = 1000
      if (isAssessing() && retryAssessing < MAX_RETRY_ASSESSING) {
        const data = await fetchCoverstatus()
        if (!isEmpty(assessingAt) && ['Quoted', 'Refused', 'Rejected'].includes(data.status))
          waitingTime = getRemainingDelay(assessingAt)
        await sleep(waitingTime).then(id => clearTimeout(id))
        setState(data.requirement, data.callbackUrl, data.status)
        setRetryAssessing(retryAssessing + 1)
      }
    }
    retryFetchCoverStatus()
  }, [quoteStatus, retryAssessing])

  useEffect(() => {
    const cancelCover = async () => {
      if(isAssessing() && retryAssessing == MAX_RETRY_ASSESSING) {
        await cancelTransaction()
        await fetchCoverstatus(setState)
      }
    }
    cancelCover()
  }, [quoteStatus, retryAssessing])

  useEffect(() => {
    if ((isEmpty(quoteStatus) && !isFraudEnabled) || isAssessing() || shouldPatchTransaction)
      setAssessingAt(Date.now())
  }, [quoteStatus])

  const handleGetQuote = async() => {
    let i = 0
    while (i < 60) {
      if (! isEmpty(document.ftrToken)) {
        const quotedAt = Date.now()
        getQuote(document.ftrToken, async(rq, url, status) => {
          if (status != 'Assessing' && !isFraudEnabled) 
            await sleep(getRemainingDelay(quotedAt)).then(id => clearTimeout(id))
          setState(rq, url, status)
        })
        return;
      }
      await sleep(1000).then(id =>  clearTimeout(id))
      i++
    }
    handleException()
  }


  const setState = async (requirement, callbackUrl, status) => {
    setQuoteStatus(status)
    setCallbackUrl(callbackUrl)
    setRequirement(requirement)
  } 

  const isAssessing = () => quoteStatus === 'Assessing'
  const isQuoted = () => quoteStatus === 'Quoted'
  const isRefused = () => quoteStatus === 'Refused' || quoteStatus === 'Rejected'
  const isRequirement = () => requirement === 'StrongVerification' || requirement === 'Otp'
  const showFraudLoader = () => isFraudEnabled && isEmpty(error) && !shouldPatchTransaction &&
      (isEmpty(quoteStatus) || (isQuoted() && isRequirement() && isEmpty(assessingAt)))
  const showAssessingLoader = () => isEmpty(quoteStatus) || isAssessing() || (!isEmpty(assessingAt) && (isRequirement()))
  const showResult = () => isRefused() || (isQuoted() && (isEmpty(requirement) || requirement === 'None'))
  const showBackUrl = () => !isEmpty(error) || showResult()
  const getRemainingDelay = (from) => isEmpty(from) ? 0 : MIN_WAITING_BEFORE_REQUIREMENT_MS - (Date.now() - from)  
  const getCallbackUrl  = () => isQuoted() ? successUrl : isRefused() ? failureUrl : undefined

  const { t } = useTranslation();
  return ( 
    <> 
      { !isResourceLoaded || showFraudLoader() ?
          <Layout><Loader loading={true} title={t('loader.default.title')} /></Layout> 
      :
      <>
        <Layout>  
          <div className="az-pay-title">
            {t("home.title")}
          </div>
          <Result renderer={showResult()} status = {isQuoted() ? 'Accepted' : 'Failed'}
            successMessage={t('home.success.text')}
            failureMessage={t('home.refused.text')} />
          <Loader loading={showAssessingLoader()} title={t('loader.default.title')}  message={t('home.loading.text')}/>
          <Error message={t('home.error.text')}/>
          <Back renderer={showBackUrl()} message={t('app.back.link')} callBackUrl={getCallbackUrl()}/>
        </Layout>
      </>
      }
    </>
  );
}

export default Quotation;